import { useMemo } from 'react';

import { TierStatusName } from '@order/graphql';
import {
  getPreferredSubscription,
  useFreeTrialPlanId,
  useSweetpassPlan,
} from '@order/hooks';

import { useSweetpassMembershipQuery } from '../../GraphQL/SweetpassMembership.generated';
import { useCancelSweetpassMembership } from '../useCancelSweetpassMembership';
import { useReactivateSweetpassMembership } from '../useReactivateSweetpassMembership';

export const useSweetpassMembershipData = () => {
  const { planPrice, billingFrequencyUnit, isLoadingPlan } = useSweetpassPlan();

  // ─── Queries ─────────────────────────────────────────────────────────

  const [response, refetchMembership] = useSweetpassMembershipQuery({
    requestPolicy: 'cache-and-network',
    pause: true,
  });

  const { freeTrialPlanId, isLoadingFreeTrialPlanId } = useFreeTrialPlanId();

  // ─── Loyalty Profile Data ───────────────────────────────────────

  const {
    data: sweetpassMembershipData,
    error: loyaltyProfileError,
    fetching,
    stale,
  } = response;

  const isSweetpassMembershipLoading =
    fetching ||
    (stale && !loyaltyProfileError) ||
    isLoadingPlan ||
    isLoadingFreeTrialPlanId;

  const loyaltyProfile = sweetpassMembershipData?.loyaltyProfile;
  const joinDate = loyaltyProfile?.joinDate;
  const tierStatus = loyaltyProfile?.tierStatus?.name;
  const tier = loyaltyProfile?.tier;
  const nextTier = loyaltyProfile?.tier?.nextTier;
  const hasPaidTierHistory = Boolean(loyaltyProfile?.hasPaidTierHistory);
  const trialEligibility = loyaltyProfile?.trialEligible?.[0]?.duration ?? 0;
  const isEligibleToFreeTrial = trialEligibility > 0;

  // ─── Subscriptions Data ─────────────────────────────────────────

  const subscriptions = sweetpassMembershipData?.getSubscriptions;
  const subscription = getPreferredSubscription(subscriptions);
  const subscriptionId = subscription?.id;
  const nextBillingDate = subscription?.nextBillingDate;

  // ─── Payment Methods Data ───────────────────────────────────────

  const paymentMethods =
    sweetpassMembershipData?.paymentSubscriptionProfile?.creditCards;

  // ─── Callbacks ──────────────────────────────────────────────────

  const { cancelSubscriptionAtCycleEnd } = useCancelSweetpassMembership({
    subscriptionId,
    nextBillingDate,
    tierName: tier?.name,
    isFreeTrial: tierStatus === TierStatusName.FreeTrial,
    refetchMembership,
  });

  const { reactivateSubscription } = useReactivateSweetpassMembership({
    subscriptionId,
    refetchMembership,
  });

  return useMemo(
    () => ({
      isSweetpassMembershipLoading,
      tierStatus,
      tier: tier ? { id: tier.id, name: tier.name } : undefined,
      nextTier: nextTier ? { id: nextTier.id, name: nextTier.name } : undefined,
      freeTrialPlanId,
      planPrice,
      billingFrequencyUnit,
      hasPaidTierHistory,
      isEligibleToFreeTrial,
      joinDate,
      activeSubscription: subscription
        ? {
            id: subscription.id,
            paidThroughDate: subscription.paidThroughDate as string | null,
            nextBillingDate: subscription.nextBillingDate,
            nextBillingPeriodAmount: subscription.nextBillingPeriodAmount,
            firstBillingDate: subscription.firstBillingDate as
              | string
              | null
              | undefined,
            billingFrequencyUnit: subscription.billingFrequencyUnit,
            price: subscription.price,
          }
        : undefined,
      paymentMethods,
      loyaltyProfileError,
      cancelSubscriptionAtCycleEnd,
      reactivateSubscription,
      refetchMembership,
    }),
    [
      isSweetpassMembershipLoading,
      tierStatus,
      tier,
      nextTier,
      freeTrialPlanId,
      planPrice,
      billingFrequencyUnit,
      hasPaidTierHistory,
      isEligibleToFreeTrial,
      joinDate,
      subscription,
      paymentMethods,
      loyaltyProfileError,
      cancelSubscriptionAtCycleEnd,
      reactivateSubscription,
      refetchMembership,
    ],
  );
};
